const QUICK_ADD_ERROR_DISPLAY_MS = 7000;
const QUICK_ADD_SUCCESS_VISUAL_DISPLAY_MS = 1500;
const QUICK_ADD_SUCCESS_LIVE_CLEAR_MS = 5000;

export class QuickAddStatusController {
  #host;
  #errorTimer = null;
  #successTimer = null;
  #successLiveTimer = null;

  constructor(host) {
    this.#host = host;
  }

  destroy() {
    this.clearError({ remove: true });
    this.clearSuccess({ remove: true });
  }

  showError(error) {
    const message = this.#errorMessage(error);
    const alert = this.#errorElement();

    this.clearSuccess();
    alert.textContent = message;
    alert.hidden = false;
    this.#host.dataset.quickAddError = 'true';

    window.clearTimeout(this.#errorTimer);
    this.#errorTimer = window.setTimeout(() => {
      this.clearError();
    }, QUICK_ADD_ERROR_DISPLAY_MS);
  }

  clearError({ remove = false } = {}) {
    window.clearTimeout(this.#errorTimer);
    this.#errorTimer = null;

    const alert = this.#host.querySelector('[data-quick-add-error]');
    if (!(alert instanceof HTMLElement)) return;

    if (remove) {
      alert.remove();
    } else {
      alert.textContent = '';
      alert.hidden = true;
    }

    delete this.#host.dataset.quickAddError;
  }

  showPending() {
    const status = this.#successElement();

    this.#showStatus({
      message: this.#pendingMessage(status),
      state: 'pending',
    });
  }

  showSuccess() {
    this.#showStatus({
      message: this.#successMessage(this.#successElement()),
      state: 'success',
      visualDisplayMs: QUICK_ADD_SUCCESS_VISUAL_DISPLAY_MS,
      liveClearMs: QUICK_ADD_SUCCESS_LIVE_CLEAR_MS,
    });
  }

  clearSuccess({ remove = false } = {}) {
    this.#clearStatusTimers();
    delete this.#host.dataset.quickAddPending;
    delete this.#host.dataset.quickAddSuccess;

    const status = this.#host.querySelector('[data-quick-add-success-status]');
    if (!(status instanceof HTMLElement)) return;

    if (remove) {
      status.remove();
    } else {
      this.#statusTextElement(status).textContent = '';
      status.classList.remove('quick-add__success--visible');
      status.hidden = true;
    }
  }

  #showStatus({ message, state, visualDisplayMs = 0, liveClearMs = 0 }) {
    const status = this.#successElement();
    const text = this.#statusTextElement(status);

    this.#clearStatusTimers();
    text.textContent = message;
    status.hidden = false;
    delete this.#host.dataset.quickAddPending;
    delete this.#host.dataset.quickAddSuccess;

    if (state === 'pending') {
      this.#host.dataset.quickAddPending = 'true';
    } else if (state === 'success') {
      this.#host.dataset.quickAddSuccess = 'true';
    }

    status.classList.add('quick-add__success--visible');

    if (visualDisplayMs > 0) {
      this.#successTimer = window.setTimeout(() => {
        this.#hideSuccessVisual();
      }, visualDisplayMs);
    }

    if (liveClearMs > 0) {
      this.#successLiveTimer = window.setTimeout(() => {
        this.clearSuccess();
      }, liveClearMs);
    }
  }

  #clearStatusTimers() {
    window.clearTimeout(this.#successTimer);
    this.#successTimer = null;
    window.clearTimeout(this.#successLiveTimer);
    this.#successLiveTimer = null;
  }

  #hideSuccessVisual() {
    window.clearTimeout(this.#successTimer);
    this.#successTimer = null;

    delete this.#host.dataset.quickAddPending;
    delete this.#host.dataset.quickAddSuccess;

    const status = this.#host.querySelector('[data-quick-add-success-status]');
    if (!(status instanceof HTMLElement)) return;

    status.classList.remove('quick-add__success--visible');
  }

  #errorElement() {
    const current = this.#host.querySelector('[data-quick-add-error]');
    if (current instanceof HTMLElement) return current;

    const alert = document.createElement('div');
    alert.className = 'quick-add__error';
    alert.dataset.quickAddError = 'true';
    alert.hidden = true;
    alert.setAttribute('role', 'alert');
    alert.setAttribute('aria-live', 'assertive');
    this.#host.append(alert);

    return alert;
  }

  #successElement() {
    const current = this.#host.querySelector('[data-quick-add-success-status]');
    if (current instanceof HTMLElement) return current;

    const status = document.createElement('div');
    status.className = 'quick-add__success';
    status.dataset.quickAddSuccessStatus = 'true';
    status.hidden = true;
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');
    this.#statusTextElement(status);
    this.#host.append(status);

    return status;
  }

  #statusTextElement(status) {
    const current = status.querySelector('[data-quick-add-status-text]');
    if (current instanceof HTMLElement) return current;

    status.textContent = '';

    const surface = document.createElement('span');
    surface.className = 'quick-add__status-surface';

    const text = document.createElement('span');
    text.className = 'quick-add__status-text';
    text.dataset.quickAddStatusText = 'true';

    surface.append(text);
    status.append(surface);

    return text;
  }

  #successMessage(status) {
    const configured = status.dataset.quickAddSuccessMessage?.trim();
    if (configured) return configured;

    const translated = window.FitCheck?.translations?.added?.trim();
    if (translated) return translated;

    return 'Added';
  }

  #pendingMessage(status) {
    const configured = status.dataset.quickAddPendingMessage?.trim();
    if (configured) return configured;

    return 'Adding';
  }

  #errorMessage(error) {
    const description = typeof error?.description === 'string' ? error.description.trim() : '';
    if (description) return description;

    const message = typeof error?.message === 'string' ? error.message.trim() : '';
    if (message) return message;

    if (error?.errors && typeof error.errors === 'object') {
      const first = Object.values(error.errors)
        .flat()
        .find((entry) => typeof entry === 'string' && entry.trim());
      if (first) return first.trim();
    }

    return window.FitCheck?.translations?.quick_add_cart_error || 'Unable to add this item to your cart.';
  }
}
